/*
 * PinManageModal — dialog for staff to set / change / clear their 4-digit
 * back-office PIN. Opened from the Staff TopBar via the "PIN set" / "Set PIN"
 * button. Reuses the same shared bcrypt endpoints the login gate uses.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { KeyRound, X, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PinManageModal({ user, authHeaders, onClose, toast }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pinSet = !!user.pin_set;

  // Esc-to-close — matches the AuthModal + backdrop behaviour so keyboard
  // users don't feel trapped in the till modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const setNew = async () => {
    setError("");
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setError("PIN must be 4 digits.");
      return;
    }
    if (pin !== confirm) {
      setError("PINs don't match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/pin/set`, { pin }, { headers: authHeaders });
      toast({ title: "PIN saved", description: "Next time, sign in with just email + PIN." });
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const clearPin = async () => {
    if (!window.confirm("Clear your PIN? You'll go back to email + password sign-in.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/auth/pin`, { headers: authHeaders });
      toast({ title: "PIN cleared" });
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={() => onClose(false)}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="staff-pin-modal"
        className="w-full max-w-md bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] p-6 md:p-8"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="section-label flex items-center gap-2"><KeyRound size={13} /> {pinSet ? "Change your PIN" : "Set a 4-digit PIN"}</div>
          <button onClick={() => onClose(false)} className="text-[var(--mm-cream-muted)] hover:text-[var(--mm-cream)]"><X size={16} /></button>
        </div>
        <p className="text-sm text-[var(--mm-cream-muted)] mb-5 font-light">
          A PIN lets you sign in at the till without typing your email password. Only staff accounts can use it.
        </p>
        <label className="block text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-2">New PIN (4 digits)</label>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          data-testid="pin-modal-new"
          className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2.5 text-lg tracking-[0.5em] text-center focus:border-[var(--mm-accent)] outline-none mb-4"
        />
        <label className="block text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-2">Confirm PIN</label>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
          data-testid="pin-modal-confirm"
          className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2.5 text-lg tracking-[0.5em] text-center focus:border-[var(--mm-accent)] outline-none mb-4"
        />
        {error && <div className="text-xs text-red-400 mb-3" data-testid="pin-modal-error">{error}</div>}
        <button
          onClick={setNew}
          disabled={busy}
          data-testid="pin-modal-save"
          className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          {busy ? "Saving..." : (pinSet ? "Update PIN" : "Save PIN")}
        </button>
        {pinSet && (
          <button
            onClick={clearPin}
            disabled={busy}
            data-testid="pin-modal-clear"
            className="w-full mt-3 text-xs tracking-[0.22em] uppercase text-red-400 hover:text-red-300 transition-colors py-2"
          >
            Clear my PIN
          </button>
        )}
      </div>
    </div>
  );
}
